import React from 'react';
import { Box, Typography, InputBase } from '@mui/material';
import { Search } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { formatMoneda, diasHasta } from '../state/format';
import { EstadoBolsa, ORDEN_KANBAN } from '../state/bolsaMachine';
import { calcularPaginacion, TamanoPagina } from '../state/paginacion';
import { BolsaCentro } from '../types';
import { StatusBadge } from './StatusBadge';
import { QuoteTablePagination } from './QuoteTablePagination';
import { EditButton } from './EditButton';

interface QuoteTableProps {
  cotizaciones: BolsaCentro[];
  filtradas: BolsaCentro[];
  filtroEstado: EstadoBolsa | 'TODAS';
  busqueda: string;
  onFiltrar: (estado: EstadoBolsa | 'TODAS') => void;
  onBuscar: (valor: string) => void;
  onAbrirDrawer: (id: string) => void;
  onVerDetalle: (id: string) => void;
}

// SPEC-110 (RN-001, RN-005) — la tipografía (`fs()`, `useIconScale`) escala con el ancho
// de viewport (clamp/breakpoint MUI), así que la altura real de una fila y del thead
// varían entre desktop y móvil (en móvil quedan ancladas al piso de `fs()`, más chicas).
// Un valor fijo en px no es preciso para ambos casos, así que estas constantes son solo
// un fallback para el primer pintado (antes de medir el DOM real) — ver `useMinAltoTabla`.
const ROW_HEIGHT_PX_FALLBACK = 70;
const HEADER_HEIGHT_PX_FALLBACK = 40;
const MIN_VISIBLE_ROWS = 7;
const TABLE_MIN_HEIGHT_PX_FALLBACK = HEADER_HEIGHT_PX_FALLBACK + ROW_HEIGHT_PX_FALLBACK * MIN_VISIBLE_ROWS;
const TABLE_MAX_HEIGHT = '70vh';

/**
 * Mide en el DOM real el alto del thead + de una fila de datos (thead y fila varían con
 * el viewport porque `fs()`/`useIconScale` escalan por ancho, incluido el piso fijo en
 * móvil <1024px) y devuelve el mínimo necesario para RN-001 (7 filas + header). Toma la
 * fila más alta entre las primeras `MIN_VISIBLE_ROWS` renderizadas (no solo la primera)
 * para no subestimar cuando una fila tiene la línea extra "vence en X días". Se remide
 * en cada cambio de filas y de tamaño de ventana (rotación de móvil, resize).
 */
function useMinAltoTabla(
  theadRef: React.RefObject<HTMLElement | null>,
  tbodyRef: React.RefObject<HTMLElement | null>,
  dependencia: unknown,
): number {
  const [minAlto, setMinAlto] = React.useState(TABLE_MIN_HEIGHT_PX_FALLBACK);

  React.useLayoutEffect(() => {
    const medir = () => {
      const altoHeader = theadRef.current?.getBoundingClientRect().height;
      const filas = tbodyRef.current
        ? Array.from(tbodyRef.current.querySelectorAll('[data-testid="quote-row"]')).slice(0, MIN_VISIBLE_ROWS)
        : [];
      const altoFilaMax = filas.reduce((max, fila) => Math.max(max, fila.getBoundingClientRect().height), 0);
      if (altoHeader && altoFilaMax) {
        setMinAlto(altoHeader + altoFilaMax * MIN_VISIBLE_ROWS);
      }
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [dependencia]);

  return minAlto;
}

const CHIPS: (EstadoBolsa | 'TODAS')[] = ['TODAS', ...ORDEN_KANBAN];
const LABEL_CHIP: Record<string, string> = {
  TODAS: 'Todas',
  [EstadoBolsa.BORRADOR]: 'Borrador',
  [EstadoBolsa.ENVIADA]: 'Enviada',
  [EstadoBolsa.GANADA]: 'Ganada',
  [EstadoBolsa.PERDIDA]: 'Perdida',
  [EstadoBolsa.VENCIDA]: 'Vencida',
  [EstadoBolsa.ANULADA]: 'Anulada',
};

export const QuoteTable: React.FC<QuoteTableProps> = ({
  filtradas,
  filtroEstado,
  busqueda,
  onFiltrar,
  onBuscar,
  onAbrirDrawer,
  onVerDetalle,
}) => {
  const searchIconSize = useIconScale(15);
  const [pagina, setPagina] = React.useState(1);
  const [filasPorPagina, setFilasPorPagina] = React.useState<TamanoPagina>(20);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const theadRef = React.useRef<HTMLElement>(null);
  const tbodyRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    setPagina(1);
  }, [filtroEstado, busqueda]);

  // SPEC-110 (RN-004) — reinicia el scroll interno de la tabla al cambiar de página o
  // de tamaño de página; la paginación en sí (SPEC-101) no se modifica.
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [pagina, filasPorPagina]);

  const { paginadas, totalPaginas } = calcularPaginacion(filtradas, pagina, filasPorPagina);

  // SPEC-110 (RN-001, RN-005) — mide header+fila reales (varían entre desktop y móvil
  // por la tipografía fluida `fs()`) en vez de asumir un valor fijo en px.
  const minAltoTablaPx = useMinAltoTabla(theadRef, tbodyRef, paginadas.map((b) => b.id).join(','));

  return (
    <Box>
      {/* SPEC-105 (CH-01) — título "Cotizaciones" ahora lo provee el SectionCard que envuelve
          este componente en CotizacionesTab; se elimina el duplicado. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${TOKENS.hair}`, paddingBottom: '6px', minWidth: 344 }}>
          <Search size={searchIconSize} color={TOKENS.faint} />
          <InputBase
            data-testid="quote-search"
            placeholder="Buscar por cliente o número…"
            value={busqueda}
            onChange={(e) => onBuscar(e.target.value)}
            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(15), flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {CHIPS.map((c) => {
            const activo = filtroEstado === c;
            return (
              <Box
                key={c}
                data-testid={`filter-chip-${c}`}
                onClick={() => onFiltrar(c)}
                sx={{
                  cursor: 'pointer',
                  padding: '5px 14px',
                  borderRadius: '999px',
                  border: `1px solid ${activo ? TOKENS.purple : TOKENS.hair}`,
                  backgroundColor: activo ? TOKENS.purple : 'transparent',
                  color: activo ? '#fff' : TOKENS.text,
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: fs(12),
                  fontWeight: 500,
                }}
              >
                {LABEL_CHIP[c]}
              </Box>
            );
          })}
        </Box>
      </Box>

      {filtradas.length === 0 ? (
        <Typography
          data-testid="quote-table-empty"
          sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13.5), color: TOKENS.faint, textAlign: 'center', padding: '70px 0' }}
        >
          No hay cotizaciones que coincidan.
        </Typography>
      ) : (
        <Box
          ref={scrollRef}
          data-testid="quote-table-scroll"
          sx={{
            minHeight: `${minAltoTablaPx}px`,
            maxHeight: TABLE_MAX_HEIGHT,
            overflowY: 'auto',
          }}
        >
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }} data-testid="quote-table">
            <Box ref={theadRef} component="thead" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
              <Box component="tr" sx={{ borderBottom: `1px solid ${TOKENS.hair}` }}>
                {['Cotización', 'Estado', 'Refs', 'Unidades', 'Validez', 'Total'].map((h, i) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: i === 5 ? 'right' : 'left',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: fs(12),
                      fontWeight: 600,
                      letterSpacing: '1.6px',
                      textTransform: 'uppercase',
                      color: TOKENS.faint,
                      padding: '10px',
                    }}
                  >
                    {h}
                  </Box>
                ))}
                {/* SPEC-105 (CH-03) — columna de acciones: encabezado vacío, ancho fijo 46px */}
                <Box component="th" sx={{ width: '46px', padding: '10px' }} />
              </Box>
            </Box>
            <Box ref={tbodyRef} component="tbody">
              {paginadas.map((b) => {
              const dias = b.validez && b.estado_codigo === EstadoBolsa.ENVIADA ? diasHasta(b.validez) : null;
              const venceProxima = dias !== null && dias >= 0 && dias <= 7;
              return (
                <Box
                  component="tr"
                  key={b.id}
                  data-testid="quote-row"
                  onClick={() => onAbrirDrawer(b.id)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom: `1px solid ${TOKENS.hair}`,
                    '&:hover': { backgroundColor: TOKENS.wash },
                  }}
                >
                  <Box component="td" sx={{ padding: '17px 10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(15), fontWeight: 600, color: TOKENS.ink }}>
                        {b.codigo_cotizacion ?? b.id}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(12), color: TOKENS.muted }}>
                      {b.cliente_razon_social ?? '—'}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ padding: '17px 10px' }}>
                    <StatusBadge estado={b.estado_codigo} />
                    {venceProxima && (
                      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), fontWeight: 600, color: TOKENS.amber }}>
                        vence en {dias} día{dias === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </Box>
                  <Box component="td" sx={{ padding: '17px 10px', fontFamily: 'Poppins, sans-serif', fontSize: fs(15), color: TOKENS.muted }}>
                    {b.lineas_count}
                  </Box>
                  <Box component="td" sx={{ padding: '17px 10px', fontFamily: 'Poppins, sans-serif', fontSize: fs(15), color: TOKENS.muted }}>
                    {b.unidades_count}
                  </Box>
                  <Box component="td" sx={{ padding: '17px 10px', fontFamily: 'Poppins, sans-serif', fontSize: fs(15), color: TOKENS.muted }}>
                    {b.validez ?? '—'}
                  </Box>
                  <Box component="td" sx={{ padding: '17px 10px', textAlign: 'right', fontFamily: 'Poppins, sans-serif', fontSize: fs(15), fontWeight: 600, color: TOKENS.ink }}>
                    {formatMoneda(b.total)}
                  </Box>
                  <Box component="td" sx={{ width: '46px', padding: '17px 10px', textAlign: 'center' }}>
                    <EditButton
                      estadoCodigo={b.estado_codigo}
                      bolsaId={b.id}
                      onVerDetalle={onVerDetalle}
                    />
                  </Box>
                </Box>
              );
            })}
            </Box>
          </Box>
        </Box>
      )}

      {filtradas.length > 0 && (
        <QuoteTablePagination
          pagina={pagina}
          totalPaginas={totalPaginas}
          filasPorPagina={filasPorPagina}
          onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
          onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
          onCambiarTamano={(tamano) => {
            setFilasPorPagina(tamano);
            setPagina(1);
          }}
        />
      )}
    </Box>
  );
};
